/**
 * Add Veeva Vault documents: pick a connection, search the vault's documents
 * (the Data browser's document listing — `POST /integration/veeva/{id}/documents`),
 * tick the ones you want. Each is pinned to the version shown, which is the
 * version the build will index.
 */

import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconInfoCircle, IconPlus, IconSearch } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { addDocuments, type AddDocumentsOut } from "@/shared/api/indexes";
import { veevaListDocuments, type DocumentSummary } from "@/shared/api/integration-browse";
import { toastApiError } from "@/shared/ui/toast";
import { VEEVA_ACCESS_NOTE, veevaDocumentsVql, versionLabel } from "./collectionMeta";
import { useConnectionsOf } from "./useConnectionsOf";

interface Props {
  indexId: string;
  onAdded: (result: AddDocumentsOut) => void;
}

export function VeevaSourceTab({ indexId, onAdded }: Props) {
  const getToken = useAccessToken();
  const connections = useConnectionsOf("veeva");
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [term, setTerm] = useState("");
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = async (id: string) => {
    setLoading(true);
    try {
      const token = await getToken();
      const result = await veevaListDocuments(token, id, veevaDocumentsVql(term));
      setDocs(result.items);
    } catch (err) {
      toastApiError("Could not list Veeva documents", err);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    if (!connectionId || !docs) return;
    setBusy(true);
    try {
      const token = await getToken();
      const chosen = docs.filter((d) => selected.has(d.id));
      onAdded(
        await addDocuments(token, indexId, {
          source_kind: "veeva",
          connection_id: connectionId,
          documents: chosen.map((d) => ({
            document_id: d.id,
            major_version: d.major_version,
            minor_version: d.minor_version,
          })),
        }),
      );
      setSelected(new Set());
    } catch (err) {
      toastApiError("Could not add Veeva documents", err);
    } finally {
      setBusy(false);
    }
  };

  const allChecked = !!docs && docs.length > 0 && docs.every((d) => selected.has(d.id));

  return (
    <Stack gap="sm">
      <Select
        label="Veeva connection"
        placeholder={connections === null ? "Loading…" : "Choose a connection"}
        data={(connections ?? []).map((c) => ({ value: c.connection_id, label: c.display_name }))}
        value={connectionId}
        onChange={(v) => {
          setConnectionId(v);
          setDocs(null);
          setSelected(new Set());
          if (v) void search(v);
        }}
        disabled={connections === null}
        nothingFoundMessage="No Veeva connections"
        searchable
      />
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="xs">
        {VEEVA_ACCESS_NOTE}
      </Alert>
      {connectionId && (
        <>
          <Group gap="xs" align="flex-end">
            <TextInput
              label="Search"
              placeholder="Words in the document name or text"
              value={term}
              onChange={(e) => setTerm(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void search(connectionId);
              }}
              style={{ flex: 1 }}
            />
            <Button
              variant="default"
              leftSection={<IconSearch size={16} />}
              onClick={() => void search(connectionId)}
              loading={loading}
            >
              Search
            </Button>
          </Group>
          {loading && docs === null ? (
            <Loader size="sm" />
          ) : docs && docs.length === 0 ? (
            <Text size="sm" c="dimmed">
              No documents found.
            </Text>
          ) : docs ? (
            <ScrollArea.Autosize mah={320}>
              <Table verticalSpacing={4} highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 36 }}>
                      <Checkbox
                        aria-label="Select all documents"
                        checked={allChecked}
                        indeterminate={!allChecked && docs.some((d) => selected.has(d.id))}
                        onChange={() =>
                          setSelected(allChecked ? new Set() : new Set(docs.map((d) => d.id)))
                        }
                      />
                    </Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Version to index</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {docs.map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td>
                        <Checkbox
                          aria-label={`Select ${d.name ?? d.id}`}
                          checked={selected.has(d.id)}
                          onChange={() => toggle(d.id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.name ?? d.filename ?? d.id}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.type ?? "—"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.status ?? "—"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{versionLabel(d)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          ) : null}
          <Group justify="flex-end">
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => void submit()}
              loading={busy}
              disabled={selected.size === 0}
            >
              Add {selected.size} document{selected.size === 1 ? "" : "s"}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
